import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

const EMPTY = { name: "", email: "", password: "", role: "admin" };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get("/admin/users").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(load, [load]);

  if (user.role !== "super_admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/users", f); toast.success("Administrateur créé"); setF(EMPTY); load(); } catch (ex) { toast.error(errMsg(ex)); }
    setBusy(false);
  };
  const remove = async () => {
    try { await api.delete(`/admin/users/${del.id}`); toast.success("Compte supprimé"); load(); } catch (ex) { toast.error(errMsg(ex)); }
    setDel(null);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Administrateurs</h1>
        <p className="text-slate-500 mt-1">Seul le super administrateur peut créer des comptes.</p>
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="rounded-2xl border border-emerald-900/10 bg-white divide-y divide-emerald-900/10">
          {items.map((u) => (
            <div key={u.id} data-testid={`admin-user-row-${u.email}`} className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                <div className="text-sm text-slate-500 truncate">{u.email}</div>
              </div>
              <span className="text-xs rounded-full border px-2 py-0.5 border-emerald-300 text-emerald-800 bg-emerald-50">{u.role === "super_admin" ? "Super admin" : "Admin"}</span>
              {u.id !== user.id && <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(u)} data-testid={`admin-user-delete-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
        <form onSubmit={submit} data-testid="admin-user-form" className="rounded-2xl border border-emerald-900/10 bg-white p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">Nouveau compte</h2>
          <div className="space-y-1.5"><Label>Nom</Label><Input required data-testid="admin-user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input required type="email" data-testid="admin-user-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Mot de passe (min. 8 caractères)</Label><Input required minLength={8} type="password" data-testid="admin-user-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Rôle</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="super_admin">Super admin</SelectItem></SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="admin-user-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Créer le compte</Button>
        </form>
      </div>
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} title="Supprimer ce compte ?" description={del?.email || ""} />
    </div>
  );
}
